import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, Fragment } from "react";
import { css } from "styled-components";

import CheckoutProductList from "@husky-hrc/Components/Checkout/CheckoutProductList";
import { propertyTruthy } from "@husky-hrc/Utilities/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    currencyCode?: string;
    discountMessage?: string;
}

type Props = OwnProps;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    productDetailsContainer?: GridContainerProps;
    itemCount?: TypographyProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsContainer?: InjectableCss;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            padding: 20px;
            margin-bottom: 32px;
        `,
    },
    productDetailsContainer: {
        gap: 10,
        css: css`
            padding: 0px 20px 20px 5px;
            border-bottom: 1.21px solid #ccccd2;
            margin-bottom: 32px;
        `,
    },
    itemCount: {
        css: css`
            font-size: 14px;
            line-height: 28px;
            color: #3a3a3a;
            font-weight: 900;
        `,
    },
    labelGridItem: { width: 6 },
    valueGridItem: { width: 6 },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsContainer: {
        css: css`
            padding: 0px 20px 20px 5px;
            border-bottom: 1.21px solid #ccccd2;
            margin-bottom: 32px;
        `,
    },
    discountsLabel: {
        as: "h3",
        css: css`
            font-size: 16px;
            line-height: 28px;
            color: #3a3a3a;
            font-weight: bold;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
            font-size: 15px;
            font-style: italic;
            font-weight: bold;
            letter-spacing: 0.2px;
            line-height: 28px;
            color: ${getColor("common.backgroundContrast")};
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabel: { as: "h3", weight: "bold" },
    totalValue: {
        weight: "bold",
        size: 18,
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
};

const CheckoutTotalDisplay: FC<Props> = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    discountTotal,
    isLoading,
    extendedStyles,
    currencyCode,
    discountMessage,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));

    const hasDiscount = propertyTruthy(cart?.properties.isDiscounted);

    if (isLoading || !cart) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Subtotal")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    if (isCartEmpty) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Subtotal")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    return (
        <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
            <GridContainer {...styles.productDetailsContainer}>
                <GridItem width={12 as GridWidths}>
                    <CheckoutProductList></CheckoutProductList>
                </GridItem>
            </GridContainer>

            {hasDiscount && (
                <StyledWrapper {...styles.discountsContainer}>
                    <GridItem width={12}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem width={12}>
                        <Typography {...styles.discountsValue}>{translate(discountMessage || "")}</Typography>
                    </GridItem>
                </StyledWrapper>
            )}
            {showTaxAndShipping && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.totalLabel}>{translate("Est. Order Total")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                            {cart.orderGrandTotalDisplay} {currencyCode}
                        </Typography>
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

export default CheckoutTotalDisplay;
